import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';

import type {
  DuplicateCheckConfig,
  DuplicateCheckResponse,
  FormValidation,
  FormValues,
  MappingJSON,
} from '../../../shared/types';

import { DUPLICATE_CHECK } from '../../../shared/constants';
import FormForgeApiService from '../../../shared/services/FormForgeApiService/FormForgeApiService';
import {
  buildFieldAttributeMaps,
  getCompositeDuplicateMessage,
  getDuplicateCheckFieldNames,
  mapAttributeResultsToFieldErrors,
  mapFieldValuesToAttributes,
} from '../../../shared/utils/fieldAttributeMapping';

const DEBOUNCE_DELAY_MS = 500;

interface UseDuplicateCheckProps {
  urlSlug: string;
  customFormUid?: string;
  /** Passed to duplicate-check APIs (e.g. update flow excludes current entity) */
  entityId?: string;
  isPublic?: boolean;
  isPreview?: boolean;
  category?: string;
  token?: string | null;
  duplicateCheckConfig?: DuplicateCheckConfig;
  fieldMapping?: MappingJSON[];
  formValues: FormValues;
  localizeError: (defaultMessage: string) => string;
  fieldNameToLabel?: Record<string, string>;
}

interface UseDuplicateCheckResult {
  duplicateErrors: Record<string, string>;
  compositeDuplicateMessage: string | null;
  isDuplicateChecking: boolean;
  duplicateCheckFieldNames: Set<string>;
  checkAllDuplicates: () => Promise<{
    singleErrors: Record<string, string>;
    compositeMessage: string | null;
  }>;
}

const EMPTY_SET = new Set<string>();
const EMPTY_ERRORS: Record<string, string> = {};

function useDuplicateCheck({
  urlSlug,
  customFormUid,
  entityId,
  isPublic = false,
  isPreview = false,
  category,
  duplicateCheckConfig,
  fieldMapping,
  formValues,
  localizeError,
  fieldNameToLabel,
}: UseDuplicateCheckProps): UseDuplicateCheckResult {
  const [duplicateErrors, setDuplicateErrors] =
    useState<Record<string, string>>(EMPTY_ERRORS);
  const [compositeDuplicateMessage, setCompositeDuplicateMessage] = useState<
    string | null
  >(null);
  const [isDuplicateChecking, setIsDuplicateChecking] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const maps = useMemo(() => {
    if (!fieldMapping?.length) {
      return null;
    }

    return buildFieldAttributeMaps(fieldMapping);
  }, [fieldMapping]);

  const duplicateCheckFieldNames = useMemo(() => {
    if (!duplicateCheckConfig || !maps) {
      return EMPTY_SET;
    }

    return getDuplicateCheckFieldNames(duplicateCheckConfig, maps);
  }, [duplicateCheckConfig, maps]);

  // Refs for values/callbacks so the debounce timer always reads the latest
  const formValuesRef = useRef(formValues);

  formValuesRef.current = formValues;

  const mapsRef = useRef(maps);

  mapsRef.current = maps;

  const localizeErrorRef = useRef(localizeError);

  localizeErrorRef.current = localizeError;

  const duplicateCheckFieldNamesRef = useRef(duplicateCheckFieldNames);

  duplicateCheckFieldNamesRef.current = duplicateCheckFieldNames;

  const configRef = useRef(duplicateCheckConfig);

  configRef.current = duplicateCheckConfig;

  const fieldNameToLabelRef = useRef(fieldNameToLabel);

  fieldNameToLabelRef.current = fieldNameToLabel;

  // Serialize only the watched field values for stable change detection.
  // This produces a primitive string so React's useEffect compares by value,
  // not reference -- preventing spurious re-runs from Formik's new object refs.
  const serializedWatchedValues = useMemo(() => {
    if (duplicateCheckFieldNames.size === 0) {
      return '';
    }

    const watchedValues: Record<string, unknown> = {};

    duplicateCheckFieldNames.forEach((fieldName) => {
      watchedValues[fieldName] = formValues[fieldName];
    });

    return JSON.stringify(watchedValues);
  }, [formValues, duplicateCheckFieldNames]);

  const executeValidation = useCallback(
    async (signal: AbortSignal): Promise<DuplicateCheckResponse | null> => {
      const currentMaps = mapsRef.current;
      const currentConfig = configRef.current;
      const currentValues = formValuesRef.current;
      const currentFieldNames = duplicateCheckFieldNamesRef.current;

      if (!currentMaps || !currentConfig) {
        return null;
      }

      // Public (unauthenticated) LiveForm: backend does not require duplicate validate
      if (isPublic) {
        return null;
      }

      if (!isPreview && !urlSlug) {
        return null;
      }

      const fieldValues = mapFieldValuesToAttributes(
        currentValues as Record<string, unknown>,
        currentFieldNames,
        currentMaps
      );

      if (fieldValues.length === 0) {
        return null;
      }

      try {
        return await FormForgeApiService.validateDuplicateCheck(
          {
            types: [DUPLICATE_CHECK.TYPE],
            urlSlug,
            ...(customFormUid ? { customFormUid } : {}),
            ...(entityId ? { entityId } : {}),
            fieldValues,
          },
          signal
        );
      } catch (error: unknown) {
        if ((error as Error)?.name === 'AbortError') {
          return null;
        }

        Logger.error('Duplicate check validation failed:', error);

        return null;
      }
    },
    [urlSlug, customFormUid, entityId, isPublic, isPreview]
  );

  const handleResponse = useCallback(
    (response: DuplicateCheckResponse | null) => {
      const currentMaps = mapsRef.current;
      const errorLocalizer = localizeErrorRef.current;
      const labels = fieldNameToLabelRef.current;

      if (!response || !currentMaps) {
        setDuplicateErrors(EMPTY_ERRORS);
        setCompositeDuplicateMessage(null);

        return;
      }

      const singleErrors = mapAttributeResultsToFieldErrors(
        response.results,
        currentMaps,
        errorLocalizer('This value already exists.')
      );

      setDuplicateErrors(
        Object.keys(singleErrors).length > 0 ? singleErrors : EMPTY_ERRORS
      );

      const compositeMsg = getCompositeDuplicateMessage(
        response.results,
        currentMaps,
        errorLocalizer('Duplicate found for combination: {fields}'),
        labels
      );

      setCompositeDuplicateMessage(compositeMsg);
    },
    []
  );

  // Debounced validation -- only re-runs when the serialized watched values
  // (a primitive string) actually change, not on every Formik re-render.
  useEffect(() => {
    if (isPublic) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      setDuplicateErrors(EMPTY_ERRORS);
      setCompositeDuplicateMessage(null);
      setIsDuplicateChecking(false);

      return undefined;
    }

    if (!serializedWatchedValues) {
      return undefined;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    debounceTimerRef.current = setTimeout(() => {
      const controller = new AbortController();

      abortControllerRef.current = controller;
      setIsDuplicateChecking(true);

      executeValidation(controller.signal)
        .then((response) => {
          if (!controller.signal.aborted) {
            handleResponse(response);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsDuplicateChecking(false);
          }
        });
    }, DEBOUNCE_DELAY_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [serializedWatchedValues, executeValidation, handleResponse, isPublic]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    []
  );

  const checkAllDuplicates = useCallback(async (): Promise<{
    singleErrors: Record<string, string>;
    compositeMessage: string | null;
  }> => {
    const currentMaps = mapsRef.current;
    const currentConfig = configRef.current;
    const currentValues = formValuesRef.current;
    const currentFieldNames = duplicateCheckFieldNamesRef.current;
    const errorLocalizer = localizeErrorRef.current;

    if (!currentMaps || !currentConfig) {
      return { singleErrors: EMPTY_ERRORS, compositeMessage: null };
    }

    if (isPublic) {
      setDuplicateErrors(EMPTY_ERRORS);
      setCompositeDuplicateMessage(null);

      return { singleErrors: EMPTY_ERRORS, compositeMessage: null };
    }

    if (!isPreview && !urlSlug) {
      return { singleErrors: EMPTY_ERRORS, compositeMessage: null };
    }

    const fieldValues = mapFieldValuesToAttributes(
      currentValues as Record<string, unknown>,
      currentFieldNames,
      currentMaps
    );

    if (fieldValues.length === 0) {
      return { singleErrors: EMPTY_ERRORS, compositeMessage: null };
    }

    try {
      setIsDuplicateChecking(true);

      let response: DuplicateCheckResponse;

      if (isPreview) {
        const validations: FormValidation[] = [
          { type: DUPLICATE_CHECK.TYPE, check: currentConfig },
        ];

        response = await FormForgeApiService.validatePreviewDuplicateCheck({
          types: [DUPLICATE_CHECK.TYPE],
          fieldValues,
          validations,
          category: category ?? '',
          ...(entityId ? { entityId } : {}),
        });
      } else {
        response = await FormForgeApiService.validateDuplicateCheck({
          types: [DUPLICATE_CHECK.TYPE],
          urlSlug,
          ...(customFormUid ? { customFormUid } : {}),
          ...(entityId ? { entityId } : {}),
          fieldValues,
        });
      }

      const singleErrors = mapAttributeResultsToFieldErrors(
        response.results,
        currentMaps,
        errorLocalizer('This value already exists.')
      );

      const compositeMessage = getCompositeDuplicateMessage(
        response.results,
        currentMaps,
        errorLocalizer('Duplicate found for combination: {fields}'),
        fieldNameToLabelRef.current
      );

      setDuplicateErrors(
        Object.keys(singleErrors).length > 0 ? singleErrors : EMPTY_ERRORS
      );
      setCompositeDuplicateMessage(compositeMessage);

      return { singleErrors, compositeMessage };
    } catch (error: unknown) {
      Logger.error('Pre-submit duplicate check failed:', error);

      return { singleErrors: EMPTY_ERRORS, compositeMessage: null };
    } finally {
      setIsDuplicateChecking(false);
    }
  }, [urlSlug, customFormUid, entityId, isPublic, isPreview, category]);

  return {
    duplicateErrors,
    compositeDuplicateMessage,
    isDuplicateChecking,
    duplicateCheckFieldNames,
    checkAllDuplicates,
  };
}

export default useDuplicateCheck;
